import { gql, TypedDocumentNode } from "@apollo/client";
import type { EventWithType } from "@/pages/events/[eventSlug]/utils";
import {
  CHANGE_REQUEST_CHANGE_FRAGMENT,
  CHANGE_REQUEST_FRAGMENT,
  ChangeRequestStatus,
  Node,
  PERSON_FRAGMENT
} from "@/pages/sections/[sectionId]/change-requests/queries";
import { SECTION_FRAGMENT } from "@/pages/sections/[sectionId]/queries";

export type DashboardEventNode = EventWithType & {
  id: string;
  slug: string;
  title: string;
  startsAt: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  registrationCount: number;
  registrationOpenAt?: string | null;
  registrationCloseAt?: string | null;
  draft: boolean;
  sessions?: { startsAt?: string | null; endsAt?: string | null; maxAttendance?: number | null }[] | null;
};

export type DashboardEventsQueryResponse = {
  events: {
    nodes: DashboardEventNode[];
  };
};

export type DashboardEventsQueryVariables = {
  first: number;
  query?: {
    status?: string[];
    sectionIds?: string[];
    dateRange?: {
      from?: string;
      to?: string;
    };
  };
  orderBy?: {
    field: "STARTS_AT" | "ENDS_AT";
    direction: "ASC" | "DESC";
  };
};

export const DASHBOARD_EVENTS_QUERY: TypedDocumentNode<DashboardEventsQueryResponse, DashboardEventsQueryVariables> =
  gql`
    query DashboardEvents(
      $query: BackofficeEventsSearchInput
      $orderBy: BackofficeEventsEventOrderByInput
      $first: Int
    ) {
      events(query: $query, orderBy: $orderBy, first: $first) {
        nodes {
          id
          slug
          title
          draft
          startsAt
          endsAt
          timezone
          registrationOpenAt
          registrationCloseAt
          registrationCount
          activityType {
            id
            title
          }
          activityTypeSummary {
            id
            title
          }
          eventType {
            id
            name
          }
          eventTypeSummary {
            id
            name
          }
          sessions {
            startsAt
            endsAt
            maxAttendance
          }
        }
      }
    }
  `;

export type RecentInteractionNode = {
  id: string;
  interactionDate: string;
  createdAt: string;
  summary: string;
  relatedTo: string;
  showAiSummary: boolean;
  participants: {
    firstName: string;
    lastName: string;
    profilePhoto: string;
    pgaId: string;
  }[];
  lastMessage: {
    content: string;
    isPrivate: boolean;
    haveAccess: boolean;
  };
};

export type RecentInteractionsQueryResponse = {
  crmInteractions: {
    totalCount: number;
    nodes: RecentInteractionNode[];
  };
};

export type RecentInteractionsInput = {
  authorId?: string;
};

export type RecentInteractionsOrderByInput = {
  field: "CREATED_AT" | "INTERACTION_DATE" | "UPDATED_AT";
  direction: "DESC";
};

export type RecentInteractionsQueryVariables = {
  first: number;
  input?: RecentInteractionsInput;
  orderBy?: RecentInteractionsOrderByInput;
};

export const DASHBOARD_PENDING_CHANGE_REQUEST_LIMIT = 3;

export type DashboardPendingChangeRequestsQueryResponse = {
  section: {
    id: string;
    permissions: {
      manageChangeRequests: boolean;
    };
    changeRequests: {
      totalCount: number;
      nodes: (Node | null)[];
    };
  };
};

export type DashboardPendingChangeRequestsQueryVariables = {
  sectionId: string;
  first: number;
  stage: ChangeRequestStatus.PENDING;
};

export const DASHBOARD_PENDING_CHANGE_REQUESTS_QUERY: TypedDocumentNode<
  DashboardPendingChangeRequestsQueryResponse,
  DashboardPendingChangeRequestsQueryVariables
> = gql`
  query DashboardPendingChangeRequests($sectionId: ID!, $first: Int!, $stage: BackofficeChangeRequestStatus!) {
    section(id: $sectionId) {
      ...SectionFragment
      changeRequests(stage: $stage, first: $first) {
        totalCount
        nodes {
          ...ChangeRequest
          submittedAt
          ...Change
          person {
            pgaId
            ...Person
          }
        }
      }
    }
  }

  ${SECTION_FRAGMENT}
  ${PERSON_FRAGMENT}
  ${CHANGE_REQUEST_FRAGMENT}
  ${CHANGE_REQUEST_CHANGE_FRAGMENT}
`;

export const RECENT_INTERACTIONS_QUERY: TypedDocumentNode<
  RecentInteractionsQueryResponse,
  RecentInteractionsQueryVariables
> = gql`
  query RecentInteractions(
    $first: Int
    $input: BackofficeCRMInteractionsInput
    $orderBy: BackofficeCRMInteractionOrderByInput
  ) {
    crmInteractions(first: $first, input: $input, orderBy: $orderBy) {
      totalCount
      nodes {
        id
        interactionDate
        createdAt
        summary
        relatedTo
        showAiSummary
        participants {
          firstName
          lastName
          profilePhoto
          pgaId
        }
        lastMessage {
          content
          isPrivate
          haveAccess
        }
      }
    }
  }
`;
